import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Particles } from '../common/effects';
import wolfLogo from '../../assets/images/polar_ace_wolf_dark.png';
import { ROUTES } from '../../constants';
import {
  authApi,
  clearStoredAuth,
  readStoredToken,
  writeStoredUser,
} from '../../services/authApi';
import { resolvePostAuthRoute } from '../../utils/postAuthRoute';
import './WelcomePage.css';

/**
 * WelcomePage — Cinematic landing page with wolf logo, particles, and Enter CTA.
 *
 * Clicking "Enter" checks for a valid stored session first.
 * Returning users with a valid token skip auth and go straight to the app.
 * Missing or invalid tokens fall back to the unified auth page.
 *
 * @component
 * @returns {JSX.Element}
 */
function WelcomePage() {
  const navigate = useNavigate();
  const [isExiting, setIsExiting] = useState(false);
  const [isChecking, setIsChecking] = useState(false);

  const navigateWithExit = (route) => {
    setIsExiting(true);
    setTimeout(() => {
      navigate(route);
    }, 1600);
  };

  useEffect(() => {
    const token = readStoredToken();
    if (!token) {
      return undefined;
    }

    let cancelled = false;

    const restoreSession = async () => {
      setIsChecking(true);

      try {
        const response = await authApi.getCurrentUser(token);
        if (response?.user) {
          writeStoredUser(response.user);
        }

        const nextRoute = await resolvePostAuthRoute();
        if (!cancelled) {
          navigate(nextRoute, { replace: true });
        }
      } catch (error) {
        if (cancelled) {
          return;
        }

        if (error?.status === 401) {
          clearStoredAuth();
          return;
        }

        const nextRoute = await resolvePostAuthRoute();
        if (!cancelled) {
          navigate(nextRoute, { replace: true });
        }
      } finally {
        if (!cancelled) {
          setIsChecking(false);
        }
      }
    };

    restoreSession();

    return () => {
      cancelled = true;
    };
  }, [navigate]);

  const handleEnter = async () => {
    if (isChecking) {
      return;
    }

    const token = readStoredToken();

    if (!token) {
      navigateWithExit(ROUTES.AUTH);
      return;
    }

    setIsChecking(true);

    try {
      const response = await authApi.getCurrentUser(token);
      if (response?.user) {
        writeStoredUser(response.user);
      }

      const nextRoute = await resolvePostAuthRoute();
      navigateWithExit(nextRoute);
    } catch (error) {
      if (error?.status === 401) {
        clearStoredAuth();
        navigateWithExit(ROUTES.AUTH);
        return;
      }

      const nextRoute = await resolvePostAuthRoute();
      navigateWithExit(nextRoute);
    } finally {
      setIsChecking(false);
    }
  };

  return (
    <main className={`welcome-page ${isExiting ? 'welcome-page--exiting' : ''}`}>
      {/* Background radial glow */}
      <div className="welcome-page__bg" aria-hidden="true" />

      {/* Ambient ice particles */}
      <div className="welcome-page__particles">
        <Particles />
      </div>

      {/* Centered content */}
      <div className="welcome-page__content">
        {/* Wolf logo with glow */}
        <div className="welcome-page__wolf-container">
          <div className="welcome-page__glow-pool" aria-hidden="true" />
          <img
            src={wolfLogo}
            alt="Polar Ace wolf logo"
            className="welcome-page__wolf"
          />
        </div>

        {/* Welcome text and CTA */}
        <div className="welcome-page__text">
          <span className="welcome-page__subtitle">Welcome to</span>
          <h1 className="welcome-page__title">Polar Ace</h1>
          <p className="welcome-page__tagline">For streamers, who want more</p>
          <button
            type="button"
            className="welcome-page__cta"
            onClick={handleEnter}
            disabled={isChecking}
            aria-label="Enter Polar Ace"
          >
            {isChecking ? 'Loading...' : 'Enter'}
          </button>
        </div>
      </div>

      {/* Bottom horizon line */}
      <div className="welcome-page__horizon" aria-hidden="true" />
    </main>
  );
}

export default WelcomePage;
